import { useQuery } from "@tanstack/react-query";
import api from "../../../../services/api";
import { consts } from "../../../../shim/jsbridge";
import { systemHealthQueryKeys } from "../QueryKeys";
import {
  ISystemHealthStatus,
  ISystemHealthStatusResponse
} from "../@types/systemhealth";

const REFRESH_RATE = consts?.system_health?.refresh_rate || 10000;
const { cloud_disabled_message } = consts || {};

type SystemHealthStatus = {
  systemHealthStatus: Partial<ISystemHealthStatus>;
  isHealthy: boolean;
  healthIssues: string[];
};

type ReducedSystemHealthStatus = {
  isHealthy: boolean;
  isCloudDisabled: boolean;
};

const DEFAULT_SYSTEM_HEALTH_STATUS: SystemHealthStatus = {
  systemHealthStatus: {},
  isHealthy: true,
  healthIssues: []
};

const DEFAULT_REDUCED_SYSTEM_HEALTH_STATUS: ReducedSystemHealthStatus = {
  isHealthy: true,
  isCloudDisabled: false
};

const useSystemHealthQuery = <T>(
  handleSelect: (data: ISystemHealthStatusResponse) => T
) =>
  useQuery({
    queryKey: systemHealthQueryKeys,
    queryFn: () =>
      api
        .systemHealthStatus()
        .then((r: { data: ISystemHealthStatusResponse }) => r.data)
        .catch(() => {
          console.log("Error encountered while fetching system health data");
        }),
    select: handleSelect,
    refetchInterval: REFRESH_RATE
  });

const selectSystemHealthStatus = (
  data: ISystemHealthStatusResponse
): SystemHealthStatus => {
  const {
    is_healthy: isHealthy,
    health_issues: healthIssues,
    health_status: systemHealthStatus
  } = data || DEFAULT_SYSTEM_HEALTH_STATUS;
  return {
    isHealthy,
    healthIssues,
    systemHealthStatus
  } as SystemHealthStatus;
};

const selectReducedSystemHealthStatus = (
  data: ISystemHealthStatusResponse
): ReducedSystemHealthStatus => ({
  isHealthy: data.is_healthy,
  isCloudDisabled: (data.health_issues || []).includes(cloud_disabled_message)
});

const useSystemHealthStatus = (): SystemHealthStatus => {
  const { data } = useSystemHealthQuery(selectSystemHealthStatus);

  return data || DEFAULT_SYSTEM_HEALTH_STATUS;
};

export const useReducedSystemHeathStatus = () => {
  const { data } = useSystemHealthQuery(selectReducedSystemHealthStatus);

  return data || DEFAULT_REDUCED_SYSTEM_HEALTH_STATUS;
};

export default useSystemHealthStatus;
